import styled from 'styled-components';
import { useState } from 'react';
import { Container, SegmentedControl, SegmentedControlItem } from '@optimus-web/core';
import { DataTestId, FeatureFlag, TextKey } from '@mews-commander/enums';
import { lt, useFeatureFlag, useSafeUrlState } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { PaymentMethodsTable } from './PaymentMethods/PaymentMethodsTable';
import { PreauthorizationsTable } from './Payments/PreauthorizationsTable';
import { PaymentsTable } from './Payments/PaymentsTable';
import { SettlementsTable } from './Settlements/SettlementsTable';
import { SettlementsScheduledBanner } from './Settlements/SettlementsScheduledBanner';
import { PaymentLimitExceededBanner } from './PaymentLimitExceededBanner';
import { PaymentPlansTable } from './PaymentPlans/PaymentPlansTable';
import { PaymentsTabActiveSegment } from './types';
import { PaymentRequestsTable } from './PaymentRequest/PaymentRequestsTable';
import { isCompanyDetailsQueryValues, paymentSegmentsQuerySchema } from './paymentSegmentsQuerySchema';

const PaymentsOverviewSegment = ({ accountId, isCompany = false }: { isCompany?: boolean } & WithAccountId) => {
    const [hasExceededBanner, setExceededBanner] = useState(false);
    const addExceededBanner = () => setExceededBanner(true);

    return (
        <>
            {!isCompany && <SettlementsScheduledBanner accountId={accountId} />}
            {hasExceededBanner && <PaymentLimitExceededBanner />}
            <PaymentsTable accountId={accountId} addExceededBanner={addExceededBanner} isCompany={isCompany} />
            <PreauthorizationsTable accountId={accountId} addExceededBanner={addExceededBanner} isCompany={isCompany} />
            {!isCompany && <SettlementsTable accountId={accountId} />}
            <PaymentsTable
                accountId={accountId}
                addExceededBanner={addExceededBanner}
                isPaymentAttemptsTable
                hideDivider
                isCompany={isCompany}
            />
        </>
    );
};

const ActiveTablesSegment = ({
    activeSegment,
    accountId,
    isCompany = false,
}: { activeSegment: PaymentsTabActiveSegment; isCompany?: boolean } & WithAccountId) => {
    switch (activeSegment) {
        case PaymentsTabActiveSegment.Overview:
            return <PaymentsOverviewSegment accountId={accountId} isCompany={isCompany} />;
        case PaymentsTabActiveSegment.Methods:
            return <PaymentMethodsTable accountId={accountId} hideDivider />;
        case PaymentsTabActiveSegment.Requests:
            return <PaymentRequestsTable accountId={accountId} isCompany={isCompany} hideDivider />;
        case PaymentsTabActiveSegment.Plans:
            return <PaymentPlansTable accountId={accountId} hideDivider />;
        default:
            return null;
    }
};

// TODO - remove after SegmentedControl is updated with similar solution
const SegmentedControlStackWrapper = styled(SegmentedControl)`
    display: inline-flex;
`;
// TODO - remove after SegmentedControl is updated with similar solution
const SegmentedControlItemWrapper = styled(SegmentedControlItem)`
    flex: auto;
`;

type Props = (
    | {
          isCompany?: false;
          segment: PaymentsTabActiveSegment;
      }
    | {
          isCompany: true;
      }
) &
    WithAccountId;

export const PaymentsTablesSegmented = (props: Props) => {
    const isPaymentPlanEnabled = useFeatureFlag(FeatureFlag.paymentPlanEnabled);
    const isNewCustomerRouteStructureEnabled = useFeatureFlag(FeatureFlag.newCustomerPageRouteStructureEnabled);

    const [urlState, setUrlState] = useSafeUrlState(paymentSegmentsQuerySchema);
    const enterpriseNavigate = useEnterpriseNavigate();

    const { accountId, isCompany } = props;

    const defaultSegment = isCompany ? urlState?.activeSegment ?? PaymentsTabActiveSegment.Overview : props.segment;
    const [activeSegment, setActiveSegment] = useState(defaultSegment);

    const segmentFromProps = !isCompany && isNewCustomerRouteStructureEnabled ? props.segment : undefined;

    const handleOnSegmentChange = (tab: PaymentsTabActiveSegment) => {
        if (!isCompany && isNewCustomerRouteStructureEnabled) {
            return void enterpriseNavigate({
                to: `Customer/$id/Payment/$activeSegment`,
                params: { id: accountId, activeSegment: tab },
            });
        }

        setActiveSegment(tab);
        setUrlState({
            ...(isCompanyDetailsQueryValues(urlState, !!isCompany) && { ...urlState }),
            activeSegment: tab,
        });
    };

    const getItemProps = (segment: PaymentsTabActiveSegment, label: TextKey) => ({
        key: segment,
        label: lt(label),
        value: segment,
        'data-test-segment': segment,
        checked: (segmentFromProps || activeSegment) === segment,
    });

    return (
        <>
            <Container padding={['100', '0']}>
                <SegmentedControlStackWrapper
                    value={segmentFromProps || activeSegment}
                    data-test-id={DataTestId.PaymentsTabSegmentControl}
                    onChange={(_, tab) => handleOnSegmentChange(tab as PaymentsTabActiveSegment)}
                >
                    <SegmentedControlItemWrapper
                        {...getItemProps(PaymentsTabActiveSegment.Overview, TextKey.Overview)}
                    />
                    <SegmentedControlItemWrapper
                        {...getItemProps(PaymentsTabActiveSegment.Requests, TextKey.RequestPlural)}
                    />
                    <SegmentedControlItemWrapper
                        {...getItemProps(PaymentsTabActiveSegment.Methods, TextKey.GeneralMethodPlural)}
                    />
                    {!isCompany && isPaymentPlanEnabled ? (
                        <SegmentedControlItemWrapper
                            {...getItemProps(PaymentsTabActiveSegment.Plans, TextKey.GeneralRecurring)}
                        />
                    ) : null}
                </SegmentedControlStackWrapper>
            </Container>
            <Container padding={['100', '0']}>
                <ActiveTablesSegment
                    activeSegment={segmentFromProps || activeSegment}
                    accountId={accountId}
                    isCompany={isCompany}
                />
            </Container>
        </>
    );
};
